(() => {
  const setupMenu = (root = document) => {
    root.querySelectorAll('[data-rp-menu-toggle]').forEach((toggle) => {
      if (toggle.dataset.rpBound) return;
      toggle.dataset.rpBound = 'true';
      const menu = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!menu) return;
      const openLabel = toggle.dataset.rpOpenLabel || toggle.getAttribute('aria-label') || 'Abrir menú';
      const closeLabel = toggle.dataset.rpCloseLabel || 'Cerrar menú';
      const setOpen = (open, restoreFocus = false) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? closeLabel : openLabel);
        menu.classList.toggle('is-open', open);
        if (restoreFocus) toggle.focus();
      };
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        setOpen(!open);
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        setOpen(false, true);
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
      const desktop = window.matchMedia('(min-width: 990px)');
      const closeOnDesktop = (event) => {
        if (event.matches) setOpen(false);
      };
      if (desktop.addEventListener) desktop.addEventListener('change', closeOnDesktop);
      else desktop.addListener(closeOnDesktop);
    });
  };

  const setupReveal = (root = document) => {
    const items = [...root.querySelectorAll('.rp-reveal:not(.is-visible)')];
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach((item) => observer.observe(item));
  };

  const setupGallery = (root = document) => {
    root.querySelectorAll('[data-rp-gallery]').forEach((gallery) => {
      if (gallery.dataset.rpBound) return;
      gallery.dataset.rpBound = 'true';
      const main = gallery.querySelector('[data-rp-gallery-main]');
      gallery.querySelectorAll('[data-rp-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!main) return;
          main.src = thumb.dataset.src;
          main.alt = thumb.dataset.alt || '';
          gallery.querySelectorAll('[data-rp-gallery-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  };

  const setupSocialGalleries = (root = document) => {
    root.querySelectorAll('[data-rp-social-gallery]').forEach((gallery) => {
      if (gallery.dataset.rpBound) return;
      gallery.dataset.rpBound = 'true';

      const viewport = gallery.querySelector('[data-rp-social-viewport]');
      const slides = [...gallery.querySelectorAll('[data-rp-social-slide]')];
      const previous = gallery.querySelector('[data-rp-social-prev]');
      const next = gallery.querySelector('[data-rp-social-next]');
      const current = gallery.querySelector('[data-rp-social-current]');
      if (!viewport || !slides.length || !previous || !next || !current) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeIndex = 0;
      let ticking = false;

      const getMetrics = () => {
        const step = slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : viewport.clientWidth;
        const maxIndex = step > 0
          ? Math.min(slides.length - 1, Math.ceil((viewport.scrollWidth - viewport.clientWidth) / step))
          : 0;
        return { step, maxIndex };
      };

      const updateState = () => {
        const { step, maxIndex } = getMetrics();
        activeIndex = step > 0 ? Math.min(maxIndex, Math.max(0, Math.round(viewport.scrollLeft / step))) : 0;
        current.textContent = String(activeIndex + 1);
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex >= maxIndex;
        ticking = false;
      };

      const move = (direction) => {
        const { step, maxIndex } = getMetrics();
        const target = Math.min(maxIndex, Math.max(0, activeIndex + direction));
        viewport.scrollTo({
          left: target * step,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      });
      viewport.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateState);
      }, { passive: true });
      window.addEventListener('resize', updateState, { passive: true });
      updateState();
    });
  };

  const money = (cents, currency = 'CLP') => new Intl.NumberFormat('es-CL', {
    style: 'currency', currency, maximumFractionDigits: 0
  }).format(cents / 100);

  const setupPacks = (root = document) => {
    root.querySelectorAll('[data-rp-product]').forEach((product) => {
      if (product.dataset.rpBound) return;
      product.dataset.rpBound = 'true';
      const price = product.querySelector('[data-rp-price]');
      const compare = product.querySelector('[data-rp-compare]');
      const save = product.querySelector('[data-rp-save]');
      const prepaid = product.querySelector('[data-rp-prepaid]');
      const unitPrice = product.querySelector('[data-rp-unit-price]');
      const onlineUnitPrice = product.querySelector('[data-rp-online-unit-price]');
      product.querySelectorAll('[data-rp-variant]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          const cents = Number(radio.dataset.price || 0);
          const prepaidCents = Number(radio.dataset.prepaid || 0);
          const compareCents = Number(radio.dataset.compare || 0);
          const currency = radio.dataset.currency || 'CLP';
          if (price) price.textContent = money(cents, currency);
          if (prepaid) prepaid.textContent = money(prepaidCents, currency);
          if (unitPrice) {
            unitPrice.textContent = radio.dataset.unitPrice || '';
            unitPrice.hidden = !radio.dataset.unitPrice;
          }
          if (onlineUnitPrice) onlineUnitPrice.textContent = radio.dataset.onlineUnitPrice ? ` · ${radio.dataset.onlineUnitPrice}` : '';
          if (compare) {
            compare.textContent = compareCents > cents ? money(compareCents, currency) : '';
            compare.hidden = !(compareCents > cents);
          }
          if (save) {
            const savingsPrefix = product.dataset.rpSavingsPrefix || 'Ahorras';
            save.textContent = compareCents > cents ? `${savingsPrefix} ${money(compareCents - cents, currency)}` : '';
            save.hidden = !(compareCents > cents);
          }

          // EasySell resolves the product variant from the loaded product URL.
          // Reloading with the selected variant keeps its checkout totals and
          // the pack chosen in this theme in one deterministic state.
          const url = new URL(window.location.href);
          if (url.searchParams.get('variant') !== radio.value) {
            url.searchParams.set('variant', radio.value);
            product.setAttribute('aria-busy', 'true');
            window.location.assign(url.toString());
          }
        });
      });
    });
  };

  let easySellObserver;

  const easySellNoCoverageCities = new Set([
    'ANTARTICA',
    'BAQUEDANO',
    'EL MELON',
    'JUAN FERNANDEZ',
    'LA NEGRA',
    'LAS NIEVES - RANCAGU',
    'OLIVAR - ALTO',
    'OLIVAR - BAJO',
    'PLACILLA - V DEL MAR',
    'TIRUA'
  ]);
  const easySellPrepaidOnlyCities = new Set(['ISLA DE PASCUA']);

  const normalizeEasySellCity = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

  const normalizeEasySellAmount = (value = '') =>
    /^\d{1,3}(?:,\d{3})+$/.test(value) ? value.replaceAll(',', '.') : value;

  const formatEasySellMoneyText = (value = '') => value
    .replace(/\$\s*([0-9][0-9.,]*)\s*CLP\b/g, (_, amount) => `$${normalizeEasySellAmount(amount)}`)
    .replace(/\$\s*(\d{1,3}(?:,\d{3})+)\b/g, (_, amount) => `$${normalizeEasySellAmount(amount)}`);

  const normalizeEasySellMoney = () => {
    const form = document.querySelector('#es-form');
    if (!form) return false;

    form.querySelectorAll('*').forEach((element) => {
      if (element.childElementCount || element.matches('script, style, textarea, input, select, option')) return;
      const formatted = formatEasySellMoneyText(element.textContent || '');
      if (formatted !== element.textContent) element.textContent = formatted;
    });
    return true;
  };

  const setCoverageButtonState = (button, blocked) => {
    if (!button) return;
    if (blocked) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.rpCoverageDisabled = 'true';
      return;
    }
    if (button.dataset.rpCoverageDisabled !== 'true') return;
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    delete button.dataset.rpCoverageDisabled;
  };

  const renderCoverageNotice = (paymentButton, message = '', type = '') => {
    let notice = document.querySelector('[data-rp-easysell-coverage-notice]');
    if (!message) {
      notice?.remove();
      return;
    }
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.rpEasysellCoverageNotice = 'true';
      notice.setAttribute('role', 'alert');
      paymentButton?.insertAdjacentElement('beforebegin', notice);
    }
    if (notice.dataset.rpCoverageType !== type) notice.dataset.rpCoverageType = type;
    if (notice.textContent !== message) notice.textContent = message;
  };

  const applyEasySellCoverage = () => {
    const citySelect = document.querySelector('#es-city, select[name="city"]');
    const onlineButton = document.querySelector('.es-checkout-btn, [class*="es-checkout_"][class*="-btn"]');
    const codButton = document.querySelector('.es-cta-btn');
    if (!citySelect || (!onlineButton && !codButton)) return false;

    const city = normalizeEasySellCity(citySelect.selectedOptions?.[0]?.textContent || citySelect.value);
    const noCoverage = easySellNoCoverageCities.has(city);
    const prepaidOnly = easySellPrepaidOnlyCities.has(city);

    setCoverageButtonState(onlineButton, noCoverage);
    setCoverageButtonState(codButton, noCoverage || prepaidOnly);

    if (noCoverage) {
      renderCoverageNotice(
        onlineButton || codButton,
        'Por ahora no despachamos a esta localidad. Elige otra ciudad para continuar.',
        'blocked'
      );
    } else if (prepaidOnly) {
      renderCoverageNotice(
        onlineButton || codButton,
        'En Isla de Pascua el pago contraentrega no está disponible. Puedes continuar con Mercado Pago.',
        'prepaid-only'
      );
    } else {
      renderCoverageNotice(onlineButton || codButton);
    }
    return true;
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches('#es-city, select[name="city"]')) return;
    applyEasySellCoverage();
  });

  const mountEasySellCart = () => {
    const openers = document.querySelectorAll('#es-popup-button.es-popup-button-cart');
    if (!openers.length) return false;

    openers.forEach((opener) => {
      const ctas = opener.closest('.cart__ctas');
      const scope = opener.closest('.cart__blocks, .drawer__footer');
      ctas?.classList.add('rp-easysell-cart-mounted');
      scope?.classList.add('rp-easysell-cart-mounted');
      if (document.body.dataset.rpCartHideNativeCheckout === 'true') {
        scope?.classList.add('rp-easysell-cart-ready');
      }

      const brandStyles = {
        background: document.body.dataset.rpCartButtonBackground || 'var(--rp-mint)',
        color: document.body.dataset.rpCartButtonText || 'var(--rp-green)',
        'border-radius': `${document.body.dataset.rpCartButtonRadius || 14}px`,
        'box-shadow': '0 12px 28px rgba(18,59,58,.16)',
        'min-height': `${document.body.dataset.rpCartButtonMinHeight || 64}px`
      };
      Object.entries(brandStyles).forEach(([property, value]) => opener.style.setProperty(property, value, 'important'));
      opener.querySelectorAll('*').forEach((item) => item.style.setProperty('color', 'inherit', 'important'));
      opener.setAttribute('role', 'button');
      opener.setAttribute('tabindex', '0');
      opener.setAttribute('aria-label', document.body.dataset.rpCartButtonLabel || 'Completar compra');
      if (!opener.dataset.rpKeyboardBound) {
        opener.dataset.rpKeyboardBound = 'true';
        opener.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          opener.click();
        });
      }
    });
    return true;
  };

  const observeEasySell = () => {
    if (easySellObserver || !document.body) return;
    easySellObserver = new MutationObserver(() => {
      mountEasySell();
      mountEasySellCart();
      normalizeEasySellMoney();
      applyEasySellCoverage();
    });
    easySellObserver.observe(document.body, { childList: true, subtree: true });
  };

  const mountEasySell = () => {
    const slot = document.querySelector('[data-rp-easysell-slot]');
    const productOpener = document.querySelector('#es-popup-button.es-popup-button-product');
    const stickyOpeners = document.querySelectorAll('#es-sticky');
    const opener = productOpener || stickyOpeners[0];
    if (!slot || !opener) return false;

    if (opener.parentElement !== slot) slot.appendChild(opener);
    if (productOpener) {
      stickyOpeners.forEach((sticky) => {
        sticky.style.setProperty('display', 'none', 'important');
        sticky.setAttribute('aria-hidden', 'true');
      });
    }
    const brandStyles = {
      background: slot.dataset.rpButtonBackground || 'var(--rp-mint)',
      color: slot.dataset.rpButtonText || 'var(--rp-green)',
      'border-radius': `${slot.dataset.rpButtonRadius || 14}px`,
      'box-shadow': '0 12px 28px rgba(18,59,58,.16)',
      'min-height': `${slot.dataset.rpButtonMinHeight || 64}px`
    };
    Object.entries(brandStyles).forEach(([property, value]) => opener.style.setProperty(property, value, 'important'));
    opener.querySelectorAll('*').forEach((item) => item.style.setProperty('color', 'inherit', 'important'));
    opener.setAttribute('role', 'button');
    opener.setAttribute('tabindex', '0');
    opener.setAttribute('aria-label', slot.dataset.rpEasysellLabel || 'Completar compra');
    slot.classList.remove('is-error');
    slot.classList.add('is-ready');
    return true;
  };

  const setupEasySell = (root = document) => {
    mountEasySellCart();
    observeEasySell();
    normalizeEasySellMoney();
    const slot = root.querySelector('[data-rp-easysell-slot]') || document.querySelector('[data-rp-easysell-slot]');
    if (!slot) {
      return;
    }
    if (mountEasySell()) return;

    if (slot.dataset.rpEasySellTimer) return;
    slot.dataset.rpEasySellTimer = 'true';
    window.setTimeout(() => {
      if (mountEasySell()) return;
      slot.classList.add('is-error');
      const status = slot.querySelector('[data-rp-easysell-status]');
      if (status) {
        const title = slot.dataset.rpEasysellErrorTitle || 'No pudimos abrir las opciones de pago.';
        const text = slot.dataset.rpEasysellErrorText || 'Recarga la página o escríbenos a recupaycl@gmail.com.';
        status.replaceChildren();
        const strong = document.createElement('strong');
        const span = document.createElement('span');
        strong.textContent = title;
        span.textContent = text;
        status.append(strong, span);
      }
    }, 6000);
  };

  const setup = (root = document) => {
    setupMenu(root);
    setupReveal(root);
    setupGallery(root);
    setupSocialGalleries(root);
    setupPacks(root);
    setupEasySell(root);
  };

  document.addEventListener('DOMContentLoaded', () => setup());
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
